import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Form, Icon, Modal } from "semantic-ui-react";

import { CUSTOM_CONFIRM_TYPES, Can, CustomConfirm, DATE_FORMATS_FNS, useCan } from "components";
import "components/CustomerModal/components/CustomerNotes/components/CustomerNoteModal/CustomerNoteModal.scss";
import ENV from "config/Env";
import { usePermanentRealTimeUser, useUploadAttachment } from "hooks";
import { createFormHelper } from "lib/Form";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { formatCurrentUTCDate, formatDate } from "utils/dateHelperFunctions";

import type { CustomerNoteModalFormValues } from "components/CustomerModal/components/CustomerNotes/components";
import type { Customer, CustomerNote, CustomerNoteAttachment, DealerLocation } from "models";

type CustomerNoteModalFormProps = {
  customer: Customer;
  hasErrors: boolean;
  isPending: boolean;
  isDeletePending: boolean;
  selectedCustomerNote: CustomerNote | null;
  location?: DealerLocation | null;
  onClose: () => void;
  onSubmit: (values: CustomerNoteModalFormValues) => void;
  onDelete: () => void;
};

const FormHelper = createFormHelper<CustomerNoteModalFormValues, CustomerNoteAttachment>();

export const CustomerNoteModalForm = ({
  customer,
  location,
  hasErrors,
  isPending,
  isDeletePending,
  selectedCustomerNote,
  onClose,
  onSubmit,
  onDelete
}: CustomerNoteModalFormProps) => {
  const { t } = useTranslation();
  const { watch, handleSubmit } = FormHelper.useFormContext();

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const { user } = usePermanentRealTimeUser();
  const { handleUploadFileAttachment, isUploadingAttachment } = useUploadAttachment("/notes/attachments/upload", ENV.carBaseURL);
  const canCreateAttachments = useCan(ACL_ACTIVITIES.Create, ACL_RESOURCES.CustomerNoteAttachments);

  const note = watch("note");
  const isCustomerNoteEnabled = location?.is_customer_note_enabled;

  const handleUpload = async (file: File): Promise<CustomerNoteAttachment | null> => {
    const extension = file.name.lastIndexOf(".") > -1 ? file.name.slice(file.name.lastIndexOf(".") + 1) : "unknown";
    const formData = new FormData();

    const date = formatDate(customer.created_on, DATE_FORMATS_FNS.yearMonthDateSlash);
    const path = `${location?.id}_${location?.name}/${date}/${customer.id}`;

    formData.append("file", file);
    formData.append("path", path);

    const url = await handleUploadFileAttachment({ formData });
    if (!url) return null;

    return {
      url,
      type: file.type || extension,
      name: file.name,
      created_on: formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat),
      username: `${user?.first_name ?? ""} ${user?.last_name ?? ""}`,
      customer_note_id: selectedCustomerNote?.id ?? -1,
      id: -1,
      created_by_id: user?.id ?? -1
    };
  };

  return (
    <div className="CustomerNoteModal">
      <Modal.Header>
        <div className="modal-header padding-container">
          <Icon className="user solid fill customer-note-icon" />
          <span>{selectedCustomerNote ? t("v8_edit_customer_note", "Edit Customer Note") : t("v8_add_customer_note", "Add Customer Note")}</span>
        </div>
      </Modal.Header>

      <Modal.Content className="padding-container">
        <Form.Field required error={!note && hasErrors}>
          <label>{t("v8_note", "Note")}</label>
          <FormHelper.Field.TextArea
            name="note"
            textAreaProps={{ placeholder: `${t("v8_type_note_here", "Type note here")}...`, rows: 5, disabled: !isCustomerNoteEnabled }}
          />
        </Form.Field>
        <div className="customer-note-checkbox-fields">
          <div className="checkbox-field">
            <FormHelper.Field.Checkbox
              name="visible_to_mechanic"
              label={t("v8_note_visible_to_mechanic", "Note visible to mechanic")}
              checkboxProps={{ toggle: true }}
              disabled={!isCustomerNoteEnabled}
            />
          </div>
          <div className="checkbox-field">
            <FormHelper.Field.Checkbox
              name="visible_on_wo"
              label={t("v8_visible_on_wo", "Visible on WO")}
              checkboxProps={{ toggle: true }}
              disabled={!isCustomerNoteEnabled}
            />
          </div>
        </div>
        <FormHelper.Field.Attachments
          name="attachments"
          display={canCreateAttachments}
          disabled={!isCustomerNoteEnabled}
          isUploading={isUploadingAttachment}
          onUpload={handleUpload}
          onPreviewAttachments={a => a.map(({ url, name, created_on, created_by_id, username }) => ({ url, name, created_on, user_id: created_by_id, username }))}
        />
      </Modal.Content>

      <Modal.Actions className="padding-container">
        <div className={selectedCustomerNote ? "update-customer-note-actions" : "add-customer-note-actions"}>
          {selectedCustomerNote && (
            <div>
              <Can I="delete" the="customer-notes">
                <Button color="red" onClick={() => setShowDeleteConfirmation(true)} loading={isDeletePending} disabled={isPending}>
                  {t("v8_delete", "Delete")}
                  <Icon className="trash" />
                </Button>
              </Can>
            </div>
          )}
          <div className="save-cancel-buttons-container">
            <Button className="light" onClick={onClose}>
              {t("v8_cancel", "Cancel")}
              <Icon className="xmark" />
            </Button>
            {isCustomerNoteEnabled && (
              <Can I={["create", "update"]} the="customer-notes">
                <Button
                  type="submit"
                  color="green"
                  onClick={handleSubmit(onSubmit)}
                  loading={isPending}
                  disabled={isPending || isDeletePending || isUploadingAttachment || !isCustomerNoteEnabled}
                >
                  {t("v8_save", "Save")}
                  <Icon className="check" />
                </Button>
              </Can>
            )}
          </div>
        </div>
      </Modal.Actions>

      <CustomConfirm
        type={CUSTOM_CONFIRM_TYPES.Danger}
        isOpen={showDeleteConfirmation}
        handleCancel={() => setShowDeleteConfirmation(false)}
        confirmMsg={t("v8_delete_confirm_message", "Are you sure that you want to delete this? You can't undo this action.")}
        handleConfirm={onDelete}
        isLoading={isDeletePending}
      />
    </div>
  );
};
